import { Component, ErrorHandler, HostListener, Inject, Injector, OnDestroy, OnInit, ChangeDetectionStrategy, computed, inject, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'

import { Observable, Subject } from 'rxjs'
import { debounceTime, filter, map, startWith, takeUntil } from 'rxjs/operators'

import { AsiakasService, AsiakkaanAvainTiedot } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { TilikarttaService } from 'app/_angular/service/tilikartta.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { AsiakkaanMaksutapa } from 'app/_jaettu-lemonator/model/asiakas'
import { Kirjanpitotili, TeeLuottokorttilaskustaKirjauksetPyynto, TeeLuottokorttilaskustaKirjauksetVastaus } from 'app/_jaettu-lemonator/model/kirjanpito'
import { LlmLuottokorttilaskuPromptRow } from 'app/_jaettu-lemonator/model/llm-luottokorttilasku'
import { callLlmLuottokorttilasku } from 'app/yllapito/llm-kirjausehdotus-testaus/llm-luottokorttilasku.client'

export interface TeeLuottokorttilaskustaKirjauksetDialogData {
  asiakas: AsiakkaanAvainTiedot
  tositeAvain: string
}

const VELKATILI_OLETUS_NUMERO = '2872'

interface LuottokorttilaskuLomake {
  maksutapa: AsiakkaanMaksutapa | null
  velkatili: Kirjanpitotili | null
  promptiId: string | null
}

@Component({
  templateUrl: './tee-luottokorttilaskusta-kirjaukset.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TeeLuottokorttilaskustaKirjauksetDialog implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  lomake: FieldTree<LuottokorttilaskuLomake>

  maksutavatObservable: Observable<AsiakkaanMaksutapa[]>
  suodatetutVelkatilitSubject = new Subject<Kirjanpitotili[]>()
  readonly promptiVaihtoehdot = signal<LlmLuottokorttilaskuPromptRow[]>([])
  readonly promptitLataa = signal(true)
  readonly commonError = signal('')

  private readonly _maksutavatLataa = signal(true)
  private readonly _tilitLataa = signal(true)
  private _kaikkiTilit: Kirjanpitotili[] = []

  readonly lataa = computed(() => this._maksutavatLataa() || this._tilitLataa() || this.promptitLataa())
  readonly promptitOnTyhja = computed(() => !this.promptitLataa() && this.promptiVaihtoehdot().length === 0)

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: TeeLuottokorttilaskustaKirjauksetDialogData,
    private _dialogRef: MatDialogRef<TeeLuottokorttilaskustaKirjauksetDialog, boolean>,
    private _asiakasService: AsiakasService,
    private _tilikarttaService: TilikarttaService,
    private _firebase: FirebaseLemonator,
    private _snackbar: MatSnackBar,
    private _validationService: FormValidationService,
    private _errorHandler: ErrorHandler,
  ) {
    this.maksutavatObservable = this._asiakasService.nykyisenAsiakkaanMaksutavatObservable.pipe(
      filter(maksutavat => maksutavat !== null && maksutavat !== undefined),
      map(maksutavat => maksutavat.filter(m => m.aktiivinen)),
    )
  }

  ngOnInit() {
    this.lomake = form(
      signal<LuottokorttilaskuLomake>({ maksutapa: null, velkatili: null, promptiId: null }),
      polku => {
        required(polku.maksutapa)
        required(polku.velkatili)
        required(polku.promptiId)
      },
      { injector: this._injector },
    )

    this._lataaPromptit()

    this._asiakasService.nykyisenAsiakkaanMaksutavatObservable
      .pipe(
        filter(maksutavat => maksutavat !== null && maksutavat !== undefined),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(() => this._maksutavatLataa.set(false))

    this._tilikarttaService.nykyisenAsiakkaanTilikartanJaPaatilikartanTilitObservable
      .pipe(
        filter(tilit => tilit !== null && tilit !== undefined),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(tilit => {
        this._tilitLataa.set(false)
        this._kaikkiTilit = tilit.filter(t => t.aktiivinen && t.numero && t.numero.length >= 4)
        const oletus = this._kaikkiTilit.find(t => t.numero === VELKATILI_OLETUS_NUMERO)
        if (oletus && !this.lomake.velkatili().value()) {
          asetaArvo(this.lomake.velkatili, oletus)
        }
        this.suodatetutVelkatilitSubject.next(this._kaikkiTilit)
      })

    arvonMuutokset(this.lomake.velkatili, { injector: this._injector })
      .pipe(debounceTime(50), startWith(''), takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        const hakusana = typeof value === 'string' ? value.toLowerCase() : ''
        const suodatetut = hakusana ? this._kaikkiTilit.filter(t => t.nimi.toLowerCase().includes(hakusana) || t.numero.toLowerCase().includes(hakusana)) : this._kaikkiTilit
        this.suodatetutVelkatilitSubject.next(suodatetut)
      })
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  private async _lataaPromptit() {
    this.promptitLataa.set(true)
    try {
      const vastaus = await callLlmLuottokorttilasku(this._firebase, 'haePromptit', {})
      if (vastaus.e) {
        this.commonError.set('Promptien haku epäonnistui')
        return
      }
      this.promptiVaihtoehdot.set(vastaus.rivit)
      if (vastaus.rivit.length > 0 && !this.lomake.promptiId().value()) {
        asetaArvo(this.lomake.promptiId, vastaus.rivit[0].promptiId)
      }
    } catch (err) {
      this._errorHandler.handleError(err)
      this.commonError.set('Promptien haku epäonnistui')
    } finally {
      this.promptitLataa.set(false)
    }
  }

  displayVelkatili = (tili: Kirjanpitotili): string => (tili && tili.numero ? `${tili.numero} ${tili.nimi}` : '')

  vertaaMaksutapoja = (a: AsiakkaanMaksutapa, b: AsiakkaanMaksutapa): boolean => !!a && !!b && a.avain === b.avain

  @HostListener('document:keydown.esc')
  peruuta() {
    this._dialogRef.close(false)
  }

  async luoKirjaukset() {
    if (this.lomake().invalid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    const maksutapa = this.lomake.maksutapa().value()
    const velkatili = this.lomake.velkatili().value()
    const promptiId = this.lomake.promptiId().value()

    const pyynto: TeeLuottokorttilaskustaKirjauksetPyynto = {
      asiakasAvain: this._data.asiakas.avain,
      tositeAvain: this._data.tositeAvain,
      maksutapaAvain: maksutapa.avain,
      velkatiliNumero: velkatili.numero,
      promptiId,
    }

    this._dialogRef.close(true)

    // Fire-and-forget: dialogi on jo suljettu. Snackbar näyttää lopputuloksen taustaprosessin valmistuttua.
    this._snackbar.open('Luottokorttilaskun AI käsittely aloitettu', 'OK', { duration: 10000 })
    try {
      const vastaus = await this._firebase.functionsCall<TeeLuottokorttilaskustaKirjauksetPyynto, TeeLuottokorttilaskustaKirjauksetVastaus>('teeLuottokorttilaskustaKirjaukset', pyynto)
      if (vastaus?.e) {
        this._snackbar.open('Luottokorttilaskun AI käsittely epäonnistui', 'OK', { duration: 10000 })
        return
      }
      const maara = vastaus?.luotujenKirjaustenMaara ?? 0
      if (maara === 0) {
        this._snackbar.open('Luottokorttilaskun AI ajo jo ei luonut yhtään kirjausta - voit yrittää uudelleen eri promptilla tai tarkistaa tositteen sisällön.', 'OK', { duration: 15000 })
        return
      }
      const tasmaytysViesti = vastaus?.tasmaytysViesti ? ` - ${vastaus.tasmaytysViesti}` : ''
      const kesto = vastaus?.tasmaytysTila === 'ei-tasmaa' ? 20000 : 15000
      this._snackbar.open(`${maara} kirjausta luotu${tasmaytysViesti}`, 'OK', { duration: kesto })
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Luottokorttilaskun AI käsittely epäonnistui. Yritä uudelleen.', 'OK', { duration: 10000 })
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
